import type {
  MatchingFieldsMount,
  MatchingFieldsMountPoint,
  MemberAccountSave,
  MemberMatchingPreferencesSave,
  MemberOptionalMatchingField,
  MemberOwnProfileView,
  MemberPrivacyElectionsSave,
  MemberRegistrationAnswers,
  MemberRegistrationStep,
  MemberRegistrationStepId,
  MemberRegistrationVocabulary,
  MemberSocialChannelId,
  MemberSocialChannelValues,
  MemberSocialChannelsSave,
} from '@h2hspark/contracts';
import { ApiRequestError } from '@h2hspark/ui';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import { answersAfterSave } from './constants';


/**
 * The registration slice's only HTTP surface (`docs/standards/code-organization.md`).
 *
 * **There is no autosave hook in this file, and that is the design.** Nothing here is called on a
 * timer, on blur or on a control's change. `useSaveRegistrationStep` is called by the wizard's
 * Continue handler and by the unsaved-changes prompt's "Save and continue", and by nothing else —
 * see `registration-wizard-page.tsx` and the API's `registration-save.ts`.
 */

export interface RegistrationWizardContract {
  readonly steps: ReadonlyArray<MemberRegistrationStep>;
  readonly stepCount: number;
  readonly fieldLimits: Record<string, { readonly min: number; readonly max: number }>;
  readonly interestTagSelection: { readonly min: number; readonly max: number };
  readonly privacyElectionDefaults: Record<string, boolean>;
  readonly whyWeAsk: Record<
    MemberRegistrationStepId,
    { readonly heading: string; readonly body: ReadonlyArray<string> }
  >;
  readonly vocabularySteps: Record<MemberRegistrationVocabulary, MemberRegistrationStepId>;
  /** `MEM-045`'s optional fields and their mount table, served publicly because two of them are on
   * step 1 — which renders before there is an account to authenticate. */
  readonly optionalMatchingFields: ReadonlyArray<MemberOptionalMatchingField>;
  readonly matchingFieldMounts: Record<MatchingFieldsMountPoint, MatchingFieldsMount>;
  readonly optionalMatchingSkipNotice: string;
  /** Terms for step 1's optional selects only. Steps 2 to 5 use the authenticated route. */
  readonly preAccountVocabularyTerms: Record<
    string,
    ReadonlyArray<{ readonly termId: string; readonly displayName: string; readonly description?: string }>
  >;
  readonly vocabularyUnavailable: {
    readonly heading: string;
    readonly body: string;
    readonly nextStep: string;
  };
  readonly saving: {
    /** `'explicit'`. There is deliberately no trigger list or interval to wire up. */
    readonly mode: string;
    readonly unsavedChangesDepartures: ReadonlyArray<{
      readonly departure: string;
      readonly rendering: string;
      readonly heading?: string;
      readonly body?: string;
      readonly save?: string;
      readonly discard?: string;
      readonly cancel?: string;
    }>;
  };
}

export interface RegistrationState {
  readonly status: 'profile_incomplete' | 'active';
  /** Where the member carries on — computed from the stored record, not from this browser. */
  readonly resumeStep: number;
  readonly currentStep: number;
  readonly revision: string | undefined;
  readonly lastSavedAt: string | undefined;
  readonly returnTo: string;
  readonly answers: MemberRegistrationAnswers;
}

export interface RegistrationVocabularies {
  readonly terms: Record<
    string,
    ReadonlyArray<{ readonly termId: string; readonly displayName: string; readonly description?: string }>
  >;
  readonly unavailableVocabularies: ReadonlyArray<MemberRegistrationVocabulary>;
  readonly blockedSteps: ReadonlyArray<MemberRegistrationStepId>;
}

export interface StartRegistrationInput {
  readonly firstName: string;
  readonly lastName: string;
  readonly role: string;
  readonly company: string;
  readonly email: string;
  readonly password?: string;
  readonly googleInitCode?: string;
  readonly returnTo?: string;
}

export interface SaveOutcome {
  /** `false` when the step had not moved. The wizard uses it only to decide what to say. */
  readonly written: boolean;
  readonly revision: string;
  readonly currentStep: number;
  readonly lastSavedAt: string | undefined;
}

/** The wizard's shape and copy. Public, and safe to fetch before an account exists. */
export function useRegistrationWizardContract(): UseQueryResult<RegistrationWizardContract> {
  return useQuery({
    queryKey: ['member-registration', 'wizard'],
    queryFn: () => apiRequest<RegistrationWizardContract>('/api/members/registration/wizard'),
    // The contract does not change between renders of one session.
    staleTime: Infinity,
  });
}

/**
 * Resume (`MEM-001` AC-5, AC-7), or learn that there is nothing to resume.
 *
 * `enabled` so it is not fired before there is a session — a signed-out visitor is on step 1 and
 * has nothing to resume.
 *
 * ## `null` is an answer, not a failure
 *
 * A signed-in visitor with no member registration is the ordinary state of **every organizer who
 * ever opens `/app/join`**, and the API says so with a 404: `resume()` raises
 * `ResourceNotFoundError('member registration')` because, correctly, there is no such record. What
 * was wrong was reading that as an error. It put a red rejection in the console twice on a screen
 * where nothing had gone wrong, and it left the wizard unable to tell "no registration yet" from
 * "the read failed" — which are the two states that decide which call step 1 should make.
 *
 * So the 404 is mapped here, at the one place that knows what it means, rather than at each
 * caller. Every other status still rejects: a 500 on this read is a real failure and must stay
 * one.
 */
export function useRegistrationState(enabled: boolean): UseQueryResult<RegistrationState | null> {
  return useQuery({
    queryKey: ['member-registration', 'state'],
    queryFn: async (): Promise<RegistrationState | null> => {
      try {
        return await apiRequest<RegistrationState>('/api/members/registration');
      } catch (error) {
        if (error instanceof ApiRequestError && error.statusCode === 404) return null;
        throw error;
      }
    },
    enabled,
    retry: false,
  });
}

/**
 * What the Member App asks of the account standing in front of it (`USR-014` AC-2, AC-5).
 *
 * The account state is spelled out here rather than imported, exactly as `RegistrationState.status`
 * above is: `ACCOUNT_STATES` lives in `apps/api/src/domains/users/account-state.ts` — it is
 * `USR-008`'s table transcribed against the story document, and that transcription is checked by a
 * test that reads the story. Copying it into `packages/contracts` would make a fourth vocabulary
 * for the module's own header to complain about. The one value this app branches on is named
 * below; the rest are here so a reader knows what else can arrive.
 */
export type AccountState =
  | 'unclaimed'
  | 'member_profile_incomplete'
  | 'profile_incomplete'
  | 'active'
  | 'suspended'
  | 'deletion_requested'
  | 'deleted';

/** The ask, as the API composes it. Every sentence is the server's (`USR-014` AC-5). */
export interface MemberProfileAsk {
  readonly heading: string;
  readonly body: string;
  readonly reassurance: string;
  readonly action: string;
}

export interface MemberAppAdmission {
  /** Always `true` — `USR-014` AC-1. There is no arm of this type that closes the door. */
  readonly admitted: true;
  readonly accountState: AccountState;
  readonly memberProfileRequired: boolean;
  readonly ask: MemberProfileAsk | null;
}

/**
 * *Does this account still owe a member profile?* — asked of the API, never worked out here.
 *
 * ## Why not simply `useRegistrationState(...) === null`
 *
 * It would give the right answer today and it is the wrong question. `USR-008`'s account state is
 * derived from a row's marks **and** its registration, in one place, so that "suspended" and
 * "deletion requested" mean the same thing on every surface; a screen that re-derived a state from
 * one of those two inputs is the second definition that module exists to have deleted. This asks
 * for the answer instead, and gets the ask's copy with it.
 *
 * `enabled` so it does not fire for a signed-out visitor, who has no account to ask about.
 */
export function useMemberAppAdmission(enabled: boolean): UseQueryResult<MemberAppAdmission> {
  return useQuery({
    queryKey: ['member-app', 'admission'],
    queryFn: () => apiRequest<MemberAppAdmission>('/api/users/me/member-app-admission'),
    enabled,
  });
}

export function useRegistrationVocabularies(enabled: boolean): UseQueryResult<RegistrationVocabularies> {
  return useQuery({
    queryKey: ['member-registration', 'vocabularies'],
    queryFn: () => apiRequest<RegistrationVocabularies>('/api/members/registration/vocabularies'),
    enabled,
  });
}

export interface StartRegistrationResult {
  readonly userId: string;
  readonly currentStep: number;
  readonly returnTo: string;
  /**
   * The session the member carries into step 2, or `null`.
   *
   * **Steps 2 to 5 are authenticated**, so without storing this the wizard would stop dead the
   * moment the account was created — which is exactly what it did before this was threaded
   * through. `null` is a real outcome (the Google path's session belongs to the social exchange;
   * local development uses the loopback dev-bypass), not an error.
   */
  readonly session: {
    readonly sessionToken: string;
    readonly refreshToken: string;
    readonly expiresAt: string;
  } | null;
}

/** Step 1. The one call that creates an account, and the reason this route is public. */
export function useStartRegistration(): UseMutationResult<
  StartRegistrationResult,
  Error,
  StartRegistrationInput
> {
  return useMutation({
    mutationFn: (input: StartRegistrationInput) =>
      apiRequest<StartRegistrationResult>('/api/members/registration', {
        method: 'POST',
        body: input,
      }),
  });
}

/**
 * What step 1 sends when the visitor is **already signed in** (`MEM-002` AC-7, `MEM-001` AC-7).
 *
 * The account's own fields are absent, and that is the whole shape of the thing: an organizer
 * opening `/app/join` is not creating an account, so there is no address to send and no password
 * to ask them for. The server takes the member's id from the session.
 */
export interface StartRegistrationForMyAccountInput {
  readonly firstName: string;
  readonly lastName: string;
  readonly role: string;
  readonly company: string;
  readonly industryTermId?: string;
  readonly careerStageTermId?: string;
  readonly returnTo?: string;
}

/**
 * Step 1 for a visitor who already has an account.
 *
 * **No `session` comes back**, because the caller already had one — which is the honest shape.
 * A `session: null` here would have looked like the signed-out call's "no session could be
 * granted" and sent the wizard to its sign-in interstitial for a member who was signed in the
 * whole time.
 */
export interface StartRegistrationForMyAccountResult {
  readonly userId: string;
  readonly currentStep: number;
  readonly returnTo: string;
}

export function useStartRegistrationForMyAccount(): UseMutationResult<
  StartRegistrationForMyAccountResult,
  Error,
  StartRegistrationForMyAccountInput
> {
  return useMutation({
    mutationFn: (input: StartRegistrationForMyAccountInput) =>
      apiRequest<StartRegistrationForMyAccountResult>('/api/members/registration/for-my-account', {
        method: 'POST',
        body: input,
      }),
  });
}

/**
 * An explicit save of one step.
 *
 * Called from Continue, and from the unsaved-changes prompt's save option. **Not** from an effect,
 * a debounce or an interval.
 */
export function useSaveRegistrationStep(): UseMutationResult<
  SaveOutcome,
  Error,
  { readonly step: MemberRegistrationStepId; readonly answers: MemberRegistrationAnswers; readonly ifRevision?: string }
> {
  return useMutation({
    mutationFn: (input) =>
      apiRequest<SaveOutcome>('/api/members/registration', {
        method: 'PATCH',
        body: input,
      }),
  });
}

export function useCompleteRegistration(): UseMutationResult<
  { readonly returnTo: string; readonly status: 'active' },
  Error,
  void
> {
  return useMutation({
    mutationFn: () =>
      apiRequest<{ readonly returnTo: string; readonly status: 'active' }>(
        '/api/members/registration/complete',
        { method: 'POST' },
      ),
  });
}

/* ============================================================================
   The four optional matching fields (`MEM-045`)
   ============================================================================ */

export interface MatchingPreferencesPayload {
  readonly fields: ReadonlyArray<MemberOptionalMatchingField>;
  readonly mounts: Record<MatchingFieldsMountPoint, MatchingFieldsMount>;
  readonly skipNotice: string;
  /** Keyed by attribute name. An absent key means the member has not answered that one. */
  readonly current: Record<string, string>;
}

const OPTIONAL_PREFERENCES_KEY = ['member-matching-preferences'] as const;

/**
 * What the panel opens with, on the mount that is not the wizard.
 *
 * The wizard mount reads the same four fields out of the registration record it is already
 * holding, so this hook exists for the preferences mount alone — one fetch, rather than the
 * wizard growing a second round trip for data it has.
 */
export function useMatchingPreferences(
  enabled: boolean,
): UseQueryResult<MatchingPreferencesPayload> {
  return useQuery({
    queryKey: OPTIONAL_PREFERENCES_KEY,
    queryFn: () => apiRequest<MatchingPreferencesPayload>('/api/members/matching-preferences'),
    enabled,
  });
}

/**
 * Save them. Explicit, like everything else a member edits in this product — called by the
 * panel's Save button and by nothing else.
 *
 * `null` for a field is an erase. The panel sends it when a member picks the empty option, which
 * is the difference between "I never said" and "I would rather not say any more".
 */
export function useSaveMatchingPreferences(): UseMutationResult<
  SaveOutcome,
  Error,
  Record<string, string | null>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<SaveOutcome>('/api/members/matching-preferences', {
        method: 'PATCH',
        body: input,
      }),
    // The saved answers go into the cache before anything reseeds from it (TW 44260267): the form
    // reseeds its draft the moment the save succeeds, and a refetch would land after that.
    onSuccess: (_outcome, input) => {
      queryClient.setQueryData<MatchingPreferencesPayload>(OPTIONAL_PREFERENCES_KEY, (cached) =>
        cached === undefined ? cached : { ...cached, current: answersAfterSave(cached.current, input) },
      );
      void queryClient.invalidateQueries({ queryKey: OPTIONAL_PREFERENCES_KEY });
    },
  });
}

/* ============================================================================
   The member's own profile and what they control (`MEM-005`…`MEM-010`)
   ============================================================================ */

/**
 * Every hook below is a **read or an explicit save**, and there is nothing in between.
 *
 * No `onChange` handler in this slice calls a mutation, no mutation is debounced, and none is
 * called from an effect. That is the rule the wizard above follows and the reason the API's
 * `profile-service.ts` takes whole screens rather than patches: `MEM-007` AC-4's "nothing saves
 * implicitly" is only true if the client has no path that would.
 */

const OWN_PROFILE_KEY = ['member', 'profile'] as const;
const SOCIAL_CHANNELS_KEY = ['member', 'social-channels'] as const;
const FULL_PREFERENCES_KEY = ['member', 'matching-preferences', 'full'] as const;
const PRIVACY_KEY = ['member', 'privacy'] as const;

/**
 * The member's own profile — the one read Your account edits and the shell's avatar draws.
 *
 * `enabled` is for the shell (TW 44131893): the header is drawn for a signed-out visitor too, and
 * asking for a profile without a session would be a 401 on every page load. Omitted, the query's
 * defaults apply, which is what every screen behind `ProtectedRoute` wants.
 */
export function useOwnProfile(
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<MemberOwnProfileView> {
  return useQuery({
    queryKey: OWN_PROFILE_KEY,
    queryFn: () => apiRequest<MemberOwnProfileView>('/api/members/me/profile'),
    retry: false,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

/**
 * **`useEventsAttended` was here, and went with `/app/profile/events`** (2026-09-12).
 *
 * It was the Events tile's list (`MEM-005` AC-2), and both the tile and the list were on the
 * profile screen Sumit had removed: *"we don't need to redirect the old profile url, it can just
 * be removed."* `GET /api/members/me/events-attended` still answers — the counts and the list are
 * kept unused for the Phase 2 screen that brings the tiles back, the way `ORG-001` AC-3's plan
 * code is kept — but nothing in this app asks it, so the hook is gone rather than left as a
 * caller nobody calls.
 */

/** `MEM-007` AC-5 — the whole screen, one operation. */
export function useSaveAccount(): UseMutationResult<MemberOwnProfileView, Error, MemberAccountSave> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<MemberOwnProfileView>('/api/members/me/account', { method: 'PUT', body: input }),
    onSuccess: (data) => {
      queryClient.setQueryData(OWN_PROFILE_KEY, data);
    },
  });
}

/**
 * `MEM-007` AC-2, AC-7. The response is the same shape whatever the address turns out to be, so
 * there is nothing here for the screen to branch on — which keeps the promise on the client as
 * well as on the server.
 */
export function useRequestEmailChange(): UseMutationResult<
  { readonly pendingEmailAddress: string; readonly notice: string },
  Error,
  { readonly emailAddress: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ readonly pendingEmailAddress: string; readonly notice: string }>(
        '/api/members/me/email-change',
        { method: 'POST', body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

export function useConfirmEmailChange(): UseMutationResult<
  { readonly emailAddress: string },
  Error,
  { readonly token: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ readonly emailAddress: string }>('/api/members/me/email-change/confirm', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

/* ============================================================================
   The deletion request (`USR-011`)
   ============================================================================ */

/**
 * **Ask for this account to be deleted** (`USR-011` AC-1).
 *
 * Both hooks invalidate the profile rather than writing the answer into the cache, because what
 * the screen needs afterwards is `private.deletionRequestedAt` — a field of the profile read, not
 * of either response. Setting it by hand here would be a second place that decides what an
 * outstanding request looks like.
 */
export function useRequestAccountDeletion(): UseMutationResult<
  { readonly requestedAt: string },
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ readonly requestedAt: string }>('/api/members/me/deletion-request', {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

/** **Withdraw it** (`USR-011` AC-3). The request is the only thing this deletes. */
export function useCancelAccountDeletion(): UseMutationResult<
  { readonly cancelled: boolean },
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ readonly cancelled: boolean }>('/api/members/me/deletion-request', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

/* ============================================================================
   Email verification (`USR-003`)
   ============================================================================ */

/**
 * Ask for a fresh verification code (`USR-003` AC-5's resend).
 *
 * **Two things about this endpoint are load-bearing and are not obvious from its name.**
 *
 * It is on the *auth* router rather than under `/api/members`, because the thing it moves is the
 * identity provider's own attribute — `GetUserAttributeVerificationCode` against the caller's
 * access token — and the member row is only written when the code comes back
 * (`public-auth-router.ts`).
 *
 * It is **authenticated**, which is what keeps `USR-003` AC-6 true of the resend without the
 * endpoint having to do anything clever: the caller names no address, so there is no address for
 * an answer to differ about. A resend that took an email in its body would be an enumeration
 * oracle no matter how carefully its response was worded.
 *
 * Cognito replaces the outstanding code when it issues a new one, so a resend invalidates the
 * previous one. That is the identity provider's behaviour and this client cannot demonstrate it —
 * see `USR-003`'s Test coverage note.
 */
export function useSendEmailVerification(): UseMutationResult<void, Error, void> {
  return useMutation({
    mutationFn: () => apiRequest<void>('/api/auth/verify-email/send', { method: 'POST' }),
  });
}

/**
 * Spend the code (`USR-003` AC-3, AC-4).
 *
 * **One call, two records.** The route sets Cognito's `email_verified` and then records the proof
 * on `user.emailVerifiedAt`, which is what every application gate reads. Before PR #265 only the
 * first happened, so a member could complete this and stay unverified as far as the product was
 * concerned. `OWN_PROFILE_KEY` is invalidated on success because `private.emailVerified` on that
 * payload is the second of the two, and the screen re-reads it to decide it is done.
 */
export function useCompleteEmailVerification(): UseMutationResult<
  void,
  Error,
  { readonly code: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<void>('/api/auth/verify-email/complete', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

/**
 * Spend the **credential link** an administrator sent (`ADM-039` AC-3, ruling 83).
 *
 * ## Why this calls an organizer route from the member app
 *
 * Because it is not an organizer route — it is *the* route that spends an `address-proof`
 * credential, and it happens to live on the organizers router because `ORG-004` built it first.
 * `OnboardingService.resendVerificationForMember` is a thin wrapper over the same private
 * `issueVerificationLink` the organizer's confirmation uses: the same one-row-per-person slot, the
 * same SHA-256 at rest, the same fourteen-day lifetime, the same `address-proof` purpose and the
 * same `confirmEmail` on the other end — which writes `user.emailVerifiedAt` *and* the directory's
 * `email_verified` through the one function that writes both. A second endpoint here would be a
 * second implementation of that, which is how somebody comes to hold a link the confirmation route
 * does not recognise. `ADM-039` AC-3 forbids exactly that ("**not a second implementation**").
 *
 * ## Unauthenticated, on purpose
 *
 * It is declared in `apps/api/src/http/edge-public-routes.ts`, because the token **is** the
 * credential (ADR 042) and the click comes from an inbox on whatever device is to hand. The
 * transport sends no bearer when there is none; a signed-in member's token rides along and is
 * ignored.
 *
 * ## One boolean, and the screen may not embroider it
 *
 * `{ confirmed: boolean }` and nothing else. Expired, already spent, superseded, or issued for an
 * address that has since moved are one answer — distinguishing them would let somebody probe which
 * accounts exist by guessing tokens. See `MEMBER_VERIFY_EMAIL_COPY.linkRefused`.
 *
 * `OWN_PROFILE_KEY` is invalidated on success for `useCompleteEmailVerification`'s reason: a
 * signed-in member who verified from the link is looking at a cached `private.emailVerified` that
 * has just stopped being true.
 */
export function useConfirmEmailVerificationLink(): UseMutationResult<
  { readonly confirmed: boolean },
  Error,
  { readonly token: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ readonly confirmed: boolean }>(
        '/api/organizers/onboarding/email-confirmations',
        { method: 'POST', body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

export interface SocialChannelsPayload {
  readonly channels: ReadonlyArray<{
    readonly id: MemberSocialChannelId;
    readonly label: string;
    readonly hint: string;
    readonly placeholder: string;
  }>;
  readonly current: MemberSocialChannelValues;
}

export function useSocialChannels(): UseQueryResult<SocialChannelsPayload> {
  return useQuery({
    queryKey: SOCIAL_CHANNELS_KEY,
    queryFn: () => apiRequest<SocialChannelsPayload>('/api/members/me/social-channels'),
    retry: false,
  });
}

export function useSaveSocialChannels(): UseMutationResult<
  { readonly current: MemberSocialChannelValues },
  Error,
  MemberSocialChannelsSave
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ readonly current: MemberSocialChannelValues }>(
        '/api/members/me/social-channels',
        { method: 'PUT', body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SOCIAL_CHANNELS_KEY });
    },
  });
}

export interface FullMatchingPreferencesPayload {
  readonly current: Record<string, unknown>;
  /** The member's own picks since retired, so the screen can draw them (TW 44260222). */
  readonly retiredSelections?: Readonly<
    Record<string, ReadonlyArray<{ readonly termId: string; readonly displayName: string }>>
  >;
  readonly interestTagSelection: { readonly min: number; readonly max: number };
  readonly singleSelectVocabularies: Record<string, string>;
}

export function useFullMatchingPreferences(): UseQueryResult<FullMatchingPreferencesPayload> {
  return useQuery({
    queryKey: FULL_PREFERENCES_KEY,
    queryFn: () =>
      apiRequest<FullMatchingPreferencesPayload>('/api/members/me/matching-preferences'),
    retry: false,
  });
}

export function useSaveFullMatchingPreferences(): UseMutationResult<
  { readonly current: Record<string, unknown> },
  Error,
  MemberMatchingPreferencesSave
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ readonly current: Record<string, unknown> }>(
        '/api/members/me/matching-preferences',
        { method: 'PUT', body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FULL_PREFERENCES_KEY });
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

export interface PrivacyPayload {
  readonly elections: MemberPrivacyElectionsSave;
  readonly electionDefinitions: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly description: string;
    readonly note: string | null;
  }>;
  /**
   * `platform-config`'s fixed text, served rather than written into this app.
   *
   * It is not a toggle — it is the sentence that sits where a toggle would, and a member reading
   * it should be reading the same string the admin console shows an operator.
   */
  readonly bidirectionalOptOutStatement: string;
  readonly notificationScopeNote: string;
}

export function usePrivacyElections(): UseQueryResult<PrivacyPayload> {
  return useQuery({
    queryKey: PRIVACY_KEY,
    queryFn: () => apiRequest<PrivacyPayload>('/api/members/me/privacy'),
    retry: false,
  });
}

export function useSavePrivacyElections(): UseMutationResult<
  { readonly elections: MemberPrivacyElectionsSave },
  Error,
  MemberPrivacyElectionsSave
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ readonly elections: MemberPrivacyElectionsSave }>('/api/members/me/privacy', {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRIVACY_KEY });
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

/* ---- the photograph (`MEM-006`) ---- */

export interface PhotoUploadAddress {
  readonly quarantineKey: string;
  readonly url: string;
  readonly method: 'PUT';
  readonly headers: Readonly<Record<string, string>>;
  readonly maximumBytes: number;
}

/**
 * The three steps of ADR 037's upload, as one mutation the form calls once.
 *
 * **The strip happens on the server, between step two and step three, and this client cannot skip
 * it.** The address minted by step one points into `quarantine/`, which is served to nobody; the
 * only way bytes become a profile photo is `complete`, which reads them back, removes every
 * embedded field and writes a *different* object. A client that stopped after the `PUT` would
 * leave an orphan the lifecycle rule collects and a profile with no photo — never an unstripped
 * photograph on a profile.
 *
 * The bare `fetch` is the one in this app that is not `apiRequest`, and it has to be: the address
 * is S3's (or the local adapter's), not the API's, and signing it means sending exactly the
 * headers it was signed for and no bearer token.
 */
export function useUploadProfilePhoto(): UseMutationResult<
  { readonly photoUrl: string },
  Error,
  File
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const address = await apiRequest<PhotoUploadAddress>('/api/members/me/photo/uploads', {
        method: 'POST',
        body: { contentType: file.type, byteSize: file.size },
      });
      const response = await fetch(address.url, {
        method: 'PUT',
        headers: address.headers,
        body: file,
      });
      if (!response.ok) throw new Error('That upload did not finish. Try again.');
      return await apiRequest<{ readonly photoUrl: string }>('/api/members/me/photo/complete', {
        method: 'POST',
        body: { quarantineKey: address.quarantineKey },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

export function useRemoveProfilePhoto(): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>('/api/members/me/photo', { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OWN_PROFILE_KEY });
    },
  });
}

/**
 * Whether a member session exists right now, asked of the server.
 *
 * Registration needs this after step 1 and cannot get it from the step-1 response: `session: null`
 * means both "this address already has an account and the password did not match" and "there is no
 * identity provider here", and `MEM-002` AC-6 is the reason the response may not distinguish them.
 * `/auth/me` answers the only question the wizard actually has — *can I make an authenticated
 * request?* — and answers it the same way steps 2 to 5 will.
 *
 * Never throws. A network failure is "no session" for this purpose: the wizard's next move is to
 * stop and offer sign-in, which is the right move whether the session is missing or unreachable.
 */
export async function memberSessionExists(): Promise<boolean> {
  try {
    await apiRequest<unknown>('/api/auth/me');
    return true;
  } catch {
    return false;
  }
}
